import React, { useState, useEffect, useRef } from 'react';

const InputFile = ({
  id,
  name,
  label,
  error = "",
  onChange,
  value,
  className = "",
  placeholder = "Seleccionar imagen...",
  disabled = false,
  size = 'md'
}) => {
  const [preview, setPreview] = useState(null);
  const [isHovering, setIsHovering] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!value) {
      setPreview(null);
      return;
    }

    if (typeof value === 'string') {
      setPreview(value);
    } else if (value instanceof File) {
      const objectUrl = URL.createObjectURL(value);
      setPreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }
  }, [value]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      onChange(file);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleContainerClick = () => {
    if (inputRef.current && !disabled) {
      inputRef.current.click();
    }
  };

  const errorClasses = error ? "border-red-400" : "border-gray-100";
  // Matching Input.jsx base styles
  const baseClasses = `${errorClasses} ${className} relative flex items-center justify-between w-full h-[50px] rounded-[15px] bg-gray-100 px-3 border-[1px] cursor-pointer sm:text-md`;

  return (
    <div className={`mb-6 ${size === 'lg' ? 'h-[50px] w-[83%] rounded-[15px] sm:text-lg' : size === 'sm' ? 'h-[40px] w-[83%] rounded-[10px] sm:text-sm' : ''}`}>
      {label && (
        <label htmlFor={id} className="block text-md font-medium text-gray-900 mb-1 ml-1">
          {label}
        </label>
      )}

      <div
        className={baseClasses}
        onClick={handleContainerClick}
      >
        <input
          type="file"
          id={id}
          name={name}
          ref={inputRef}
          onChange={handleFileChange}
          accept="image/*"
          className="hidden"
          disabled={disabled}
        />

        {/* Content */}
        <div className="flex items-center flex-1 overflow-hidden">
          {/* Upload Icon */}
          <div className="text-gray-500 mr-3">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
            </svg>
          </div>

          <span className={`text-md truncate ${value ? 'text-gray-900' : 'text-gray-500'}`}>
            {value ? (value.name || "Imagen cargada") : placeholder}
          </span>
        </div>

        {/* Actions when value exists */}
        {value && (
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {/* Preview Tooltip Trigger */}
            <div
              className="relative flex items-center justify-center p-1 hover:bg-gray-200 rounded-full transition-colors cursor-help"
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gray-500">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
              </svg>

              {/* Tooltip */}
              {isHovering && preview && (
                <div className="absolute top-full mt-2 right-[-10px] z-50 p-2 bg-white rounded-lg shadow-xl border border-gray-200 w-48 h-48 flex items-center justify-center animate-in fade-in zoom-in duration-200">
                  <img src={preview} alt="Preview" className="max-w-full max-h-full object-contain rounded" />
                  {/* Arrow */}
                  <div className="absolute bottom-full right-2 -mb-1 border-8 border-transparent border-b-white"></div>
                </div>
              )}
            </div>

            {/* Delete Button */}
            <button
              onClick={handleClear}
              className="p-1 hover:bg-red-100 rounded-full text-gray-500 hover:text-red-600 transition-colors"
              type="button"
              aria-label="Eliminar imagen"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {error && (
        <p className="mt-1 pl-2 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default InputFile;